import React, { useState } from "react";

import { Modal, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

import NewProjectButton from "Components/NewProjectButton";
import { APP_NAME } from "Constants/documentationUrls";
import Button, { ButtonWrapper, Link } from "ds/Button";
import * as Sticker from "ds/Sticker";
import useQueryParams from "Hooks/useQueryStrings";
import getUrl from "Libs/getUrl";
import WhiteMigrateImage from "Vendor_resources/images/github-white.svg";
import MigrateImage from "Vendor_resources/images/github.svg";

import { useSetupStepperContext } from "../Context";
import { useGithubApp } from "../hooks";
import * as S from "../pages/onboardingType/OnboardingType.styles";
import { GithubAppErrorCode } from "../types";

export const GithubOption = ({ isModal, organizationName, organizationId }) => {
  const intl = useIntl();
  const queryParameters = useQueryParams("error_code");
  const errorCode = queryParameters.error_code as GithubAppErrorCode;
  const [isMigrateFlowModalOpen, setIsMigrateFlowModalOpen] = useState(
    errorCode ? true : false
  );
  const {
    installGithubApp,
    githubOrganizations,
    error,
    isLoading,
    fetchGithubApp
  } = useGithubApp();
  const { setupStepperValues } = useSetupStepperContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkTheme = (theme as { name: string })?.name === "dark";
  const repositoryUrl = new URL(
    window.location.origin +
      getUrl({
        key: "organization.createProject.repository",
        props: {
          organizationId:
            organizationName || setupStepperValues.organization.name
        }
      })
  );
  repositoryUrl.searchParams.set("onboardingType", "migrate");

  const handleClose = () => {
    setIsMigrateFlowModalOpen(!isMigrateFlowModalOpen);
    const params = new URLSearchParams();
    params.delete("error_code");
  };

  function connectWithGithub() {
    if (githubOrganizations?.length !== 0) {
      navigate(repositoryUrl.pathname + repositoryUrl.search);
      return;
    }
    installGithubApp({
      organizationId: organizationId || setupStepperValues.organization.value,
      organizationName: organizationName || setupStepperValues.organization.name
    });
  }

  if (!githubOrganizations && !error && !isLoading) {
    fetchGithubApp({
      organizationId: organizationId || setupStepperValues.organization.value
    });
  }
  return (
    <>
      {isMigrateFlowModalOpen && (
        <Modal
          isOpen={isMigrateFlowModalOpen}
          onOpenChange={() => {
            setIsMigrateFlowModalOpen(!isMigrateFlowModalOpen);
            const params = new URLSearchParams();
            params.delete("error_code");
          }}
          handleClose={handleClose}
          size="custom"
          width="max-content"
        >
          {intl.messages[`onboarding.migrate.error.${errorCode}`] ? (
            <Sticker.Root priority="critical">
              <Sticker.Icon priority="critical" />
              <Sticker.Text>
                {intl.formatMessage(
                  {
                    id: `onboarding.migrate.error.${errorCode}`
                  },
                  {
                    link: chunks => (
                      <Link
                        analyticId="links.documentation.integrations.github"
                        href={intl.formatMessage({
                          id: "links.documentation.integrations.github"
                        })}
                      >
                        {chunks}
                      </Link>
                    )
                  }
                )}
              </Sticker.Text>
            </Sticker.Root>
          ) : (
            <>
              <Typography.Title tag="h3">
                {intl.formatMessage(
                  { id: "onboarding.migrate.permissions.notice" },
                  { app_name: APP_NAME }
                )}
              </Typography.Title>
              <ul>
                <li>
                  <Typography.BodyText size="small">
                    {intl.formatMessage(
                      {
                        id: "onboarding.migrate.permissions.1"
                      },
                      { app_name: APP_NAME }
                    )}
                  </Typography.BodyText>
                </li>
                <li>
                  <Typography.BodyText size="small">
                    {intl.formatMessage(
                      {
                        id: "onboarding.migrate.permissions.2"
                      },
                      { app_name: APP_NAME }
                    )}
                  </Typography.BodyText>
                </li>
                <li>
                  <Typography.BodyText size="small">
                    {intl.formatMessage(
                      {
                        id: "onboarding.migrate.permissions.3"
                      },
                      { app_name: APP_NAME }
                    )}
                  </Typography.BodyText>
                </li>
                <li>
                  <Typography.BodyText size="small">
                    {intl.formatMessage(
                      {
                        id: "onboarding.migrate.permissions.4"
                      },
                      { app_name: APP_NAME }
                    )}
                  </Typography.BodyText>
                </li>
              </ul>
              <ButtonWrapper justifyContent="end" spacing="modal">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  analyticId="cancel"
                >
                  {intl.formatMessage({
                    id: "cancel"
                  })}
                </Button>
                <Button
                  onClick={() => {
                    connectWithGithub();
                  }}
                  analyticId="onboarding.migrate.github.connect"
                >
                  {intl.formatMessage({
                    id: "onboarding.migrate.github.connect"
                  })}
                </Button>
              </ButtonWrapper>
            </>
          )}
        </Modal>
      )}
      <S.Card>
        <S.CardContent>
          <S.CardTitle>
            <img
              src={isDarkTheme ? WhiteMigrateImage : MigrateImage}
              alt="illustration of a migration flow"
            />
            <S.Title>
              {intl.formatMessage(
                { id: "migrate.title" },
                { appName: APP_NAME }
              )}
            </S.Title>
          </S.CardTitle>
          <Typography.BodyText size="small">
            {intl.formatMessage(
              { id: "migrate.description" },
              { appName: APP_NAME }
            )}
          </Typography.BodyText>
        </S.CardContent>
        {!isModal ? (
          <S.NewProjecutButtonContainer>
            <NewProjectButton
              organizationId={organizationName}
              linkVariant="outline"
              customLabel={intl.formatMessage({ id: "migrate.create" })}
              onAuthorized={() => {
                if (githubOrganizations?.length !== 0) {
                  navigate(repositoryUrl.pathname + repositoryUrl.search);
                  return;
                }
                setIsMigrateFlowModalOpen(true);
              }}
            />
          </S.NewProjecutButtonContainer>
        ) : (
          <Button
            analyticId="migrate.create"
            id="create-from-github-sync-project"
            variant="outline"
            onClick={() => {
              if (githubOrganizations?.length !== 0) {
                navigate(repositoryUrl.pathname + repositoryUrl.search);
                return;
              }
              setIsMigrateFlowModalOpen(true);
            }}
          >
            {intl.formatMessage({ id: "migrate.create" })}
          </Button>
        )}
      </S.Card>
    </>
  );
};
